import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BellRing, Loader2, CheckCircle2 } from "lucide-react";

const pretty = (s) => (s ? s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "");
const SLOTS = { morning: "Morning (8am–12pm)", afternoon: "Afternoon (12pm–5pm)", evening: "Evening (5pm–9pm)" };

// Shown only after a submitted search returns zero cleaners: capture the demand, don't lose the visitor.
export default function DemandCapture({ filters }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [name, setName] = useState(user?.name || "");
  const [postcode, setPostcode] = useState(filters.postcode || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sent || busy) return;
    if (!email.trim()) { toast.error("Add an email so we can let you know."); return; }
    if (!postcode.trim()) { toast.error("Add the postcode you need a cleaner in."); return; }
    setBusy(true);
    try {
      await api.post("/demand", {
        postcode: postcode.trim(), service: filters.service, date: filters.date, slot: filters.slot,
        context: "search", email: email.trim(), name: name.trim(), notes: notes.trim(),
        notify_me: true, results_count: 0,
      });
      setSent(true);
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="mt-6 bg-card rounded-2xl border border-accent/30 p-6 text-center" data-testid="demand-sent">
        <CheckCircle2 className="h-8 w-8 text-accent mx-auto" />
        <p className="font-display font-semibold mt-3">You're on the list! ✅</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
          We're recruiting cleaners in {postcode.toUpperCase() || "your area"} and we'll let you know
          as soon as suitable coverage becomes available.
        </p>
      </div>
    );
  }

  const carried = [
    filters.service && ["Service", pretty(filters.service)],
    filters.date && ["Date", filters.date],
    filters.slot && ["Time", SLOTS[filters.slot] || pretty(filters.slot)],
  ].filter(Boolean);

  return (
    <form onSubmit={submit} className="mt-6 bg-secondary/40 rounded-2xl border border-primary/5 p-6" data-testid="demand-capture">
      <div className="flex items-center gap-2">
        <BellRing className="h-5 w-5 text-accent" />
        <h3 className="font-display font-semibold">Help Us Help You</h3>
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        Leave your details and we'll email you as soon as we have cover in your area.
      </p>

      {carried.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="demand-carried">
          {carried.map(([k, v]) => (
            <span key={k} className="px-2.5 py-1 rounded-full bg-background border border-border text-xs font-medium">
              {k}: {v}
            </span>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mt-4">
        <input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode (e.g. NN7 2QL)" className="px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="demand-postcode" />
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="demand-name" />
      </div>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className="w-full mt-3 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="demand-email" />
      <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything else? e.g. weekly Airbnb turnover, Saturday mornings" className="w-full mt-3 px-3 py-2.5 rounded-xl border border-border bg-background outline-none focus:border-accent text-sm" data-testid="demand-notes" />
      <button type="submit" disabled={busy} className="mt-4 w-full sm:w-auto px-6 py-2.5 rounded-full bg-accent text-white font-medium text-sm hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="demand-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Notify me when cleaners are available
      </button>
    </form>
  );
}
